<template>
  <div class="glass-container">
    <div class="gift-card-view">
      <!-- Main Container -->
      <b-container fluid class="main-container">
        <!-- Header -->
        <b-row>
          <b-col>
            <div class="modern-card header-card">
              <div class="header-content">
                <div class="header-left">
                  <div class="icon-wrapper">
                    <b-icon-gift class="title-icon" />
                  </div>
                  <div>
                    <h3 class="page-title">Gift Card Details</h3>
                    <p class="page-subtitle">View gift card information and transaction history</p>
                  </div>
                </div>
                <div class="header-stats" v-if="giftCard">
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0s;">
                    <b-icon-cash class="stat-icon" />
                    <span class="chip-label">Amount</span>
                    <span class="chip-value">${{ formatNumber(giftCard.amount) }}</span>
                  </div>
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0.1s;">
                    <b-icon-wallet class="stat-icon" />
                    <span class="chip-label">Balance</span>
                    <span class="chip-value">${{ formatNumber(giftCard.remaining_balance) }}</span>
                  </div>
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0.2s;">
                    <b-icon-circle-fill class="stat-icon" :style="{ color: getStatusColor(giftCard.status) }" />
                    <span class="chip-label">Status</span>
                    <span class="chip-value">{{ giftCard.status }}</span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        
        <!-- Loading State -->
        <b-row v-if="loading" class="mt-4">
          <b-col class="text-center py-5">
            <div class="loading-container">
              <b-spinner variant="primary" large />
              <p class="mt-3 text-muted">Loading gift card details...</p>
            </div>
          </b-col>
        </b-row>
        
        <!-- Gift Card Details -->
        <b-row v-else-if="giftCard" class="mt-4">
          <b-col lg="4" xl="4">
            <b-card class="glass-card info-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-gift class="header-icon" />
                    </div>
                    <h5 class="card-header-title">Gift Card</h5>
                  </div>
                  <b-badge :variant="getStatusVariant(giftCard.status)" class="status-badge-lg">
                    <b-icon-circle-fill class="status-dot" />
                    {{ giftCard.status }}
                  </b-badge>
                </div>
              </template>
              
              <b-card-body class="card-body-custom">
                <!-- Gift Card Display -->
                <div class="gift-card-display" :class="'theme-' + getTheme()">
                  <div class="card-header-display">
                    <span class="store-name">FURNITURE MECCA</span>
                    <span class="card-badge">GIFT CARD</span>
                  </div>
                  <div class="card-body-display">
                    <div class="card-amount">${{ formatNumber(giftCard.amount) }}</div>
                    <div class="card-code">{{ giftCard.code }}</div>
                    <div class="card-message" v-if="getDesignMessage()">
                      "{{ getDesignMessage() }}"
                    </div>
                  </div>
                  <div class="card-footer-display">
                    <span v-if="giftCard.valid_until">Valid until: {{ formatDate(giftCard.valid_until) }}</span>
                    <span v-else>No expiration</span>
                    <span v-if="giftCard.assigned_to_email"> • {{ giftCard.assigned_to_email }}</span>
                  </div>
                </div>
                
                <hr class="my-3" />
                
                <!-- Status Section -->
                <div class="status-section">
                  <div class="status-item">
                    <span class="status-label"><b-icon-coin class="status-icon" /> Remaining Balance</span>
                    <span class="status-value highlight">${{ formatNumber(giftCard.remaining_balance) }}</span>
                  </div>
                  <div class="status-item">
                    <span class="status-label"><b-icon-arrow-repeat class="status-icon" /> Used Count</span>
                    <span class="status-value">{{ giftCard.used_count || 0 }} / {{ giftCard.max_uses || 1 }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.assigned_to_email">
                    <span class="status-label"><b-icon-envelope class="status-icon" /> Assigned To</span>
                    <span class="status-value">{{ giftCard.assigned_to_email }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.assigned_to_name">
                    <span class="status-label"><b-icon-person class="status-icon" /> Assigned Name</span>
                    <span class="status-value">{{ giftCard.assigned_to_name }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.category_name">
                    <span class="status-label"><b-icon-tag class="status-icon" /> Category</span>
                    <span class="status-value">{{ giftCard.category_name }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.vendor_name">
                    <span class="status-label"><b-icon-shop class="status-icon" /> Vendor</span>
                    <span class="status-value">{{ giftCard.vendor_name }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.location_name">
                    <span class="status-label"><b-icon-geo-alt class="status-icon" /> Location</span>
                    <span class="status-value">{{ giftCard.location_name }}</span>
                  </div>
                  <div class="status-item">
                    <span class="status-label"><b-icon-calendar3 class="status-icon" /> Created</span>
                    <span class="status-value">{{ formatDate(giftCard.created_at) }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.redeemed_at">
                    <span class="status-label"><b-icon-clock class="status-icon" /> Redeemed</span>
                    <span class="status-value">{{ formatDate(giftCard.redeemed_at) }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.redeemed_by_name">
                    <span class="status-label"><b-icon-person-check class="status-icon" /> Redeemed By</span>
                    <span class="status-value">{{ giftCard.redeemed_by_name }}</span>
                  </div>
                  <div class="status-item" v-if="giftCard.notes">
                    <span class="status-label"><b-icon-file-text class="status-icon" /> Notes</span>
                    <span class="status-value">{{ giftCard.notes }}</span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
          
          <b-col lg="8" xl="8">
            <b-card class="glass-card table-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-clock-history class="header-icon" />
                    </div>
                    <h5 class="card-header-title">
                      Transaction History
                    </h5>
                  </div>
                  <div class="header-actions">
                    <span class="transaction-count" v-if="transactions.length > 0">
                      {{ transactions.length }} transaction{{ transactions.length > 1 ? 's' : '' }}
                    </span>
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      @click="loadTransactions"
                      :disabled="transactionsLoading"
                    >
                      <b-spinner small v-if="transactionsLoading" />
                      <b-icon-arrow-repeat v-else class="mr-1" />
                      Refresh
                    </b-button>
                  </div>
                </div>
              </template>
              
              <b-card-body class="card-body-custom table-body-wrapper">
                <div class="table-scroll-container">
                  <b-table
                    :items="transactions"
                    :fields="transactionFields"
                    striped
                    hover
                    responsive
                    class="glass-table modern-table"
                    :busy="transactionsLoading"
                  >
                    <template #table-busy>
                      <div class="text-center py-4">
                        <b-spinner variant="primary" small />
                        <p class="mt-2 text-muted">Loading transactions...</p>
                      </div>
                    </template>
                    
                    <template #cell(transaction_type)="data">
                      <b-badge :variant="getTransactionVariant(data.value)" class="transaction-badge">
                        <b-icon :icon="getTransactionIcon(data.value)" class="transaction-icon" />
                        {{ data.value }}
                      </b-badge>
                    </template>
                    
                    <template #cell(amount_used)="data">
                      <span :class="data.value > 0 ? 'text-danger' : 'text-success'" class="amount-change">
                        {{ data.value > 0 ? '-' : '+' }}${{ formatNumber(Math.abs(data.value)) }}
                      </span>
                    </template>
                    
                    <template #cell(remaining_balance)="data">
                      <span class="balance-value">${{ formatNumber(data.value) }}</span>
                    </template>
                    
                    <template #cell(transaction_date)="data">
                      <span class="date-value">{{ formatDate(data.value) }}</span>
                    </template>
                  </b-table>
                </div>
                
                <!-- Empty State -->
                <div v-if="transactions.length === 0 && !transactionsLoading" class="empty-state">
                  <div class="empty-state-icon">
                    <b-icon-inbox font-scale="3" />
                  </div>
                  <h3>No Transactions</h3>
                  <p>This gift card hasn't been used yet</p>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
        
        <!-- Not Found -->
        <b-row v-else class="mt-4">
          <b-col class="text-center py-5">
            <div class="not-found-container">
              <b-icon-exclamation-triangle scale="3" variant="warning" class="not-found-icon" />
              <h4 class="mt-3">Gift Card Not Found</h4>
              <p class="text-muted">The gift card you're looking for doesn't exist or has been removed.</p>
              <b-button variant="primary" @click="$router.push('/gift-card/management')" class="mt-2">
                <b-icon-arrow-left class="mr-1" />
                Back to Management
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import moment from 'moment';

export default {
  name: 'GiftCardView',
  props: ['id'],
  data() {
    return {
      loading: true,
      transactionsLoading: false,
      giftCard: null,
      transactions: [],
      transactionFields: [
        { key: 'transaction_date', label: 'Date', sortable: true },
        { key: 'transaction_type', label: 'Type' },
        { key: 'amount_used', label: 'Amount' },
        { key: 'remaining_balance', label: 'Balance' },
        { key: 'performed_by', label: 'Performed By' },
        { key: 'notes', label: 'Notes' }
      ]
    };
  },
  
  created() {
    this.loadGiftCard();
  },
  
  methods: {
    formatNumber(value) {
      if (value === undefined || value === null) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    
    getTheme() {
      if (!this.giftCard) return 'classic';
      if (!this.giftCard.design_data) return 'classic';
      return this.giftCard.design_data.theme || 'classic';
    },
    
    getDesignMessage() {
      if (!this.giftCard) return null;
      if (!this.giftCard.design_data) return null;
      return this.giftCard.design_data.message || null;
    },
    
    getStatusColor(status) {
      const colors = {
        'active': '#48bb78',
        'sent': '#4299e1',
        'redeemed': '#a0aec0',
        'expired': '#f56565',
        'cancelled': '#f56565',
        'pending': '#ed8936'
      };
      return colors[status] || '#a0aec0';
    },
    
    async loadGiftCard() {
      this.loading = true;
      
      try {
        const response = await Requestor.getAsyncP(`/api/giftcard/get_by_code/${this.id}/`, {}, this);
        if (response.data.result) {
          this.giftCard = response.data.result;
          // Ensure design_data exists
          if (!this.giftCard.design_data) {
            this.giftCard.design_data = { theme: 'classic', message: '' };
          }
          this.loadTransactions();
        } else {
          this.giftCard = null;
        }
      } catch (error) {
        console.error('Error loading gift card:', error);
        this.$bvToast.toast('Failed to load gift card details', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadTransactions() {
      this.transactionsLoading = true;
      
      try {
        const response = await Requestor.getAsyncP('/api/giftcard/get_transactions/', {
          gift_card_id: this.giftCard.id
        }, this);
        if (response.data.result) {
          this.transactions = response.data.result;
        }
      } catch (error) {
        console.error('Error loading transactions:', error);
        this.$bvToast.toast('Failed to load transactions', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        this.transactionsLoading = false;
      }
    },
    
    getStatusVariant(status) {
      const variants = {
        'active': 'success',
        'sent': 'primary',
        'redeemed': 'secondary',
        'expired': 'danger',
        'cancelled': 'danger',
        'pending': 'warning'
      };
      return variants[status] || 'secondary';
    },
    
    getTransactionVariant(type) {
      const variants = {
        'issue': 'success',
        'redeem': 'danger',
        'refund': 'warning',
        'expire': 'secondary',
        'created': 'info',
        'sent': 'primary'
      };
      return variants[type] || 'secondary';
    },
    
    getTransactionIcon(type) {
      const icons = {
        'issue': 'plus-circle',
        'redeem': 'dash-circle',
        'refund': 'arrow-counterclockwise',
        'expire': 'clock',
        'created': 'check-circle',
        'sent': 'envelope'
      };
      return icons[type] || 'circle';
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MM/DD/YYYY hh:mm A');
    }
  }
};
</script>

<style scoped>
/* ============================================
   GLASS CONTAINER
   ============================================ */
.glass-container {
  min-height: 100vh;
  padding: 20px;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.glass-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 0;
}

.gift-card-view {
  padding: 0;
  background: transparent;
  min-height: 100vh;
  z-index: 1;
  position: relative;
}

.main-container {
  padding: 24px;
  position: relative;
  z-index: 1;
}

/* ============================================
   MODERN CARD
   ============================================ */
.modern-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin: 0 auto;
  margin-top: 10px;
  max-width: 1800px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-card {
  padding: 20px 28px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4a90e2, #667eea);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: #6c7a8d;
  margin: 2px 0 0;
}

.header-stats {
  display: flex;
  gap: 12px;
}

.stat-chip {
  background: rgba(255, 255, 255, 0.8);
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  animation: slideUp 0.6s ease forwards;
  opacity: 0;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  opacity: 0;
  animation: slideUp 0.6s ease forwards;
}

.stat-icon { font-size: 14px; color: #4a6cf7; }
.chip-label { color: #6c7a8d; }
.chip-value { font-weight: 700; color: #1a1a2e; }

/* ============================================
   GLASS CARDS
   ============================================ */
.glass-card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  height: 100%;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-icon-wrapper {
  width: 32px;
  height: 32px;
  background: rgba(74, 108, 247, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6cf7;
}

.card-header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.card-body-custom {
  padding: 24px;
}

/* ============================================
   GIFT CARD DISPLAY
   ============================================ */
.gift-card-display {
  border-radius: 12px;
  padding: 20px;
  background: white;
  text-align: center;
  border: 1px solid rgba(220, 226, 236, 0.3);
  transition: all 0.3s ease;
}

.gift-card-display:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.gift-card-display .card-header-display {
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid #4a6cf7;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.gift-card-display .store-name {
  font-weight: bold;
  font-size: 16px;
  color: #0b1a30;
}

.gift-card-display .card-badge {
  background: #4a6cf7;
  color: white;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.gift-card-display .card-body-display {
  padding: 15px 0;
}

.gift-card-display .card-amount {
  font-size: 36px;
  font-weight: 700;
  color: #2c3e50;
}

.gift-card-display .card-code {
  font-size: 16px;
  font-family: 'Courier New', monospace;
  background: rgba(0, 0, 0, 0.04);
  padding: 5px 10px;
  border-radius: 4px;
  letter-spacing: 1px;
  display: inline-block;
  margin: 10px 0;
}

.gift-card-display .card-message {
  font-style: italic;
  color: #6c757d;
  font-size: 13px;
}

.gift-card-display .card-footer-display {
  font-size: 12px;
  color: #6c757d;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 10px;
}

/* Theme: Modern */
.theme-modern .gift-card-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.theme-modern .gift-card-display .store-name {
  color: white;
}

.theme-modern .gift-card-display .card-amount {
  color: white;
}

.theme-modern .gift-card-display .card-footer-display {
  color: rgba(255, 255, 255, 0.8);
  border-top-color: rgba(255, 255, 255, 0.2);
}

.theme-modern .gift-card-display .card-code {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.theme-modern .gift-card-display .card-message {
  color: rgba(255, 255, 255, 0.8);
}

/* Theme: Elegant */
.theme-elegant .gift-card-display {
  border: 2px solid #d4af37;
  background: #fefcf5;
}

.theme-elegant .gift-card-display .card-header-display {
  border-bottom-color: #d4af37;
}

.theme-elegant .gift-card-display .card-badge {
  background: #d4af37;
}

/* Theme: Holiday */
.theme-holiday .gift-card-display {
  background: linear-gradient(135deg, #c31432 0%, #240b36 100%);
  color: white;
}

.theme-holiday .gift-card-display .store-name {
  color: white;
}

.theme-holiday .gift-card-display .card-amount {
  color: #ffd700;
}

.theme-holiday .gift-card-display .card-footer-display {
  color: rgba(255, 255, 255, 0.8);
  border-top-color: rgba(255, 255, 255, 0.2);
}

.theme-holiday .gift-card-display .card-code {
  background: rgba(255, 255, 255, 0.15);
  color: #ffd700;
}

.theme-holiday .gift-card-display .card-message {
  color: rgba(255, 255, 255, 0.8);
}

/* ============================================
   STATUS SECTION
   ============================================ */
.status-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 20px;
}

.status-item {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  border-bottom: 1px solid rgba(220, 226, 236, 0.2);
}

.status-item:last-child {
  border-bottom: none;
}

.status-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #8a9aa8;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-icon {
  font-size: 12px;
  color: #4a6cf7;
}

.status-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin-top: 2px;
  word-break: break-word;
}

.status-value.highlight {
  color: #4a6cf7;
  font-size: 16px;
}

.status-badge-lg {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  font-size: 8px;
}

/* ============================================
   TABLE SCROLL CONTAINER
   ============================================ */
.table-body-wrapper {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-scroll-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  min-height: 300px;
  padding: 0;
}

.table-scroll-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4a90e2, #667eea);
  border-radius: 10px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #3a7bc8, #5a67d8);
}

/* Table styling */
.glass-table {
  margin-bottom: 0;
  width: 100%;
}

.glass-table >>> thead th {
  background: rgba(244, 247, 252, 0.8) !important;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(8px);
}

.glass-table >>> tbody td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: rgba(226, 232, 240, 0.3);
}

.glass-table >>> tbody tr {
  background: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.glass-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.6) !important;
}

/* ============================================
   TRANSACTION BADGE
   ============================================ */
.transaction-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
  text-transform: capitalize;
}

.transaction-icon {
  font-size: 12px;
}

.amount-change {
  font-weight: 600;
}

.balance-value {
  font-weight: 600;
  color: #2d3748;
}

.date-value {
  color: #718096;
  font-size: 0.85rem;
}

.transaction-count {
  font-size: 12px;
  color: #718096;
  padding: 4px 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 20px;
}

/* ============================================
   LOADING
   ============================================ */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state-icon {
  color: #a0aec0;
  margin-bottom: 15px;
}

.empty-state h3 {
  color: #2d3748;
  margin-bottom: 8px;
  font-weight: 600;
}

.empty-state p {
  color: #718096;
  margin-bottom: 15px;
}

/* ============================================
   NOT FOUND
   ============================================ */
.not-found-container {
  padding: 60px 20px;
}

.not-found-icon {
  color: #ed8936;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 992px) {
  .status-section {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .main-container { padding: 16px; }
  .page-title { font-size: 20px; }
  .header-content { flex-direction: column; align-items: flex-start; }
  .header-stats { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
  .stat-chip { flex: 1; min-width: 80px; justify-content: center; }
  .status-section { grid-template-columns: 1fr; }
  .card-header-custom { flex-direction: column; align-items: flex-start; gap: 8px; }
  .header-actions { width: 100%; }
  .table-scroll-container {
    max-height: 350px;
    min-height: 200px;
  }
  .gift-card-display .card-amount {
    font-size: 28px;
  }
}

@media (max-width: 480px) {
  .header-stats { flex-direction: column; }
  .stat-chip { width: 100%; justify-content: center; }
  .table-scroll-container {
    max-height: 300px;
    min-height: 150px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/GiftCard/GiftCardView.vue